import {
  DiFormat,
  LossFunction,
  Model,
  ModelType,
  QiBoundType,
} from 'types/dca-scenarios-api/responses';
import {
  BiasedProductionStreamConfigData,
  EarlyTimeProductionStreamConfigData,
  GeneralConfig,
  OutlierConfig,
  ProductionStreamConfigData,
  RampUpProductionStreamConfigData,
  SegmentationConfig,
} from './types';
import { isEqual } from 'lodash-es';
import { defaultModelConfigValues } from './default-values';

export const getMethodParameterMapping = (
  modelType: string
): Record<string, string[]> => ({
  modified_hyperbolic: ['qi', 'di', 'bFactor', 'dlim'],
  hyperbolic: ['qi', 'di', 'bFactor'],
  exponential: modelType === 'ramp_up' ? ['qi', 'n'] : ['qi', 'di'],
  ratio: ['ri', 'tElf', 'm'],
  linear: ['qi', 'n'],
  constant: ['qi'],
});

// Helper to filter parameter object to only include necessary parameters
const filterParametersByModel = (
  parameters: Record<string, any>,
  model: Model,
  modelType: ModelType,
  lossFunction: LossFunction | 'mlle',
  constrainEur: boolean
): Record<string, any> => {
  const allowedParams = getMethodParameterMapping(modelType)[model];
  const filtered: Record<string, any> = {};

  if (modelType === 'biased' && lossFunction === 'mlle' && constrainEur) {
    allowedParams.push('eurMbbl');
  }

  allowedParams.forEach((param) => {
    if (parameters && parameters[param] !== undefined) {
      if (parameters[param] !== null) {
        filtered[param] =
          parameters[param] === 'none' ? null : parameters[param];
      }
    }
  });

  return filtered;
};

export const getParameterRange = (
  name: string,
  dependencies: {
    model: Model;
    modelType: ModelType;
    diFormat: DiFormat;
    qiBoundType: QiBoundType;
  }
): [number, number] => {
  const { model, modelType, diFormat, qiBoundType } = dependencies;

  switch (name) {
    case 'qi':
      if (modelType === 'ramp_up' && model === 'linear') {
        if (qiBoundType === 'relative') return [-999.99, 999.99];
        return [-100000000, 100000000];
      }
      if (qiBoundType === 'relative') return [0.01, 999.99];
      if (modelType === 'ramp_up') return [0, 100000000];
      return [1, 100000000];

    case 'di':
    case 'dlim':
      if (diFormat === 'effective') {
        return [0.01, 99.99];
      }
      return [0.0001, 0.9999];

    case 'n':
      if (modelType === 'ramp_up' && model === 'linear') {
        return [-1000000, 1000000];
      }
      if (modelType === 'ramp_up' && model === 'exponential') {
        return [0.0001, 9.9999];
      }
      return [0, 100000];

    case 'bFactor':
      return [0.01, 9.99];

    case 'ri':
      return [0.01, 10000];

    case 'tElf':
      return [1, 10000];

    case 'm':
      return [-1, 1];

    default:
      return [0, 100000000];
  }
};

export const getChangedObject = (
  current: Record<string, any> | null,
  baseline: Record<string, any> | null,
  includeId: boolean = false
): Record<string, any> => {
  if (!current) return {};

  const result: Record<string, any> = {};
  for (const key of Object.keys(current)) {
    if (!isEqual(current[key], baseline?.[key])) {
      result[key] = current[key];
    }
  }

  if (includeId && Object.keys(result).length > 0 && current.id) {
    result.id = current.id;
  }

  return result;
};

export const getChangedArray = (
  currentArray: Record<string, any>[],
  baselineArray: Record<string, any>[]
): Record<string, any>[] => {
  const result: Record<string, any>[] = [];

  for (let i = 0; i < currentArray.length; i++) {
    const current = currentArray[i];
    const baseline = baselineArray[i];
    const changed: Record<string, any> = {};

    for (const key of Object.keys(current)) {
      if (!isEqual(current[key], baseline?.[key])) {
        changed[key] = current[key];
      }
    }

    if (Object.keys(changed).length > 0) {
      if (!changed.id) changed.id = current.id;
      result.push(changed);
    }
  }

  return result;
};

export const getParameterLabel = (
  name: string,
  model: Model,
  modelType: ModelType
) => {
  switch (name) {
    case 'qi':
      return 'qi bbl/month';
    case 'di':
      return 'Di %/year';
    case 'bFactor':
      return 'b-factor';
    case 'dlim':
      return 'Dlim %/year';
    case 'ri':
      return 'Ri bbl/bbl';
    case 'tElf':
      return 't_elf months';
    case 'm':
      return 'm';
    case 'n':
      if (modelType === 'ramp_up' && model === 'linear') {
        return 'n bbl/month^2';
      } else if (modelType === 'ramp_up' && model === 'exponential') {
        return 'n 1/month';
      } else {
        return 'n';
      }
    case 'eurMbbl':
      return 'EUR Mbbl';
    default:
      return name;
  }
};

export const sanitizeProductionStreamConfig = (
  config: ProductionStreamConfigData
) => {
  return {
    stream: config.stream,
    modelType: config.modelType,
    model: config.model,
    qiBoundType: config.qiBoundType,
    lossFunction: config.lossFunction,
    residualFunction: config.residualFunction,
    alpha: config.alpha,
    maxMonthsOnline: config.maxMonthsOnline,

    initialGuess: filterParametersByModel(
      config.initialGuess,
      config.model,
      config.modelType,
      config.lossFunction,
      config.modelType === 'biased' && config.constrainEur
    ),
    lowerBounds: filterParametersByModel(
      config.lowerBounds,
      config.model,
      config.modelType,
      config.lossFunction,
      config.modelType === 'biased' && config.constrainEur
    ),
    upperBounds: filterParametersByModel(
      config.upperBounds,
      config.model,
      config.modelType,
      config.lossFunction,
      config.modelType === 'biased' && config.constrainEur
    ),
    lockedParameters: filterParametersByModel(
      config.lockedParameters,
      config.model,
      config.modelType,
      config.lossFunction,
      config.modelType === 'biased' && config.constrainEur
    ),
    ...(config.modelType === 'biased' &&
      config.lossFunction === 'mlle' && {
        distributions: filterParametersByModel(
          config.distributions,
          config.model,
          config.modelType,
          config.lossFunction,
          config.constrainEur
        ),
        residualSigma: config.residualSigma,
        probabilityDistance: config.probabilityDistance,
        constrainEur: config.constrainEur,
      }),
    ...(config.modelType === 'early_time' && {
      residualSigma: config.residualSigma,
      probabilityDistance: config.probabilityDistance,
      eurToleranceFactor: config.eurToleranceFactor,
      lateralLengthDegradationFactor: config.lateralLengthDegradationFactor,
      lateralLengthDegradationFt: config.lateralLengthDegradationFt,
    }),
    ...(config.modelType === 'ramp_up' && {
      earlyTimeRampUpOnly: config.earlyTimeRampUpOnly,
    }),
  };
};

export const getNextMaxMonthsOnline = (
  configs: ProductionStreamConfigData[],
  modelType: string
) => {
  const nextMaxMonthsOnline = configs
    .filter((config) => config.modelType === modelType)
    .reduce(
      (acc, curr) => {
        if (curr.maxMonthsOnline >= acc) {
          return curr.maxMonthsOnline + 1;
        } else {
          return acc;
        }
      },
      modelType === 'early_time' ? 12 : 10000
    );

  return nextMaxMonthsOnline;
};

export const isGeneralConfigModified = (
  current: GeneralConfig,
  baseline: GeneralConfig
): boolean => {
  if (current.historyCutoffMonths !== baseline.historyCutoffMonths) return true;
  if (current.earlyTimeCutoffMonths !== baseline.earlyTimeCutoffMonths)
    return true;
  if (current.detectPeakRate !== baseline.detectPeakRate) return true;
  if (
    !isEqual(
      current.productionDataSourceHierarchy,
      baseline.productionDataSourceHierarchy
    )
  )
    return true;
  if (current.detectPeakRate) {
    if (current.peakRateSearchWindow !== baseline.peakRateSearchWindow)
      return true;
  }
  return false;
};

const SEGMENTATION_METHOD_PARAMS: Record<string, string[]> = {
  pelt: ['penalty', 'segmentMinLength', 'jumpSize'],
  cusum: ['model'],
};

function pickRelevantSegmentationParams(
  config: SegmentationConfig
): Record<string, unknown> {
  const { id: _id, parameters, ...rest } = config;
  const allowedParams = SEGMENTATION_METHOD_PARAMS[config.method] ?? [];
  const filteredParams: Record<string, unknown> = {};

  for (const key of allowedParams) {
    if (parameters && key in parameters) {
      filteredParams[key] = parameters[key as keyof typeof parameters];
    }
  }

  return { ...rest, parameters: filteredParams };
}

export const isSegmentationConfigModified = (
  current: SegmentationConfig,
  baseline: SegmentationConfig
): boolean => {
  return !isEqual(
    pickRelevantSegmentationParams(current),
    pickRelevantSegmentationParams(baseline)
  );
};

const OUTLIER_METHOD_PARAMS: Record<string, string[]> = {
  elliptic_envelope: ['contamination'],
  n_sigma: ['probabilityThreshold', 'numIterations'],
};

function pickRelevantOutlierParams(
  config: OutlierConfig
): Record<string, unknown> {
  const { id: _id, parameters, ...rest } = config;
  const allowedParams = OUTLIER_METHOD_PARAMS[config.method ?? ''] ?? [];
  const filteredParams: Record<string, unknown> = {};

  for (const key of allowedParams) {
    if (parameters && key in parameters) {
      filteredParams[key] = parameters[key as keyof typeof parameters];
    }
  }

  return { ...rest, parameters: filteredParams };
}

export const isOutlierConfigModified = (
  current: OutlierConfig,
  baseline: OutlierConfig
): boolean => {
  return !isEqual(
    pickRelevantOutlierParams(current),
    pickRelevantOutlierParams(baseline)
  );
};

export const isProductionStreamConfigModified = (
  current: ProductionStreamConfigData,
  baseline: ProductionStreamConfigData
): boolean => {
  return !isEqual(
    sanitizeProductionStreamConfig(current),
    sanitizeProductionStreamConfig(baseline)
  );
};

export const mergedProductionStreamConfig = (
  config: ProductionStreamConfigData
): ProductionStreamConfigData => {
  if (config.modelType === 'biased') {
    const defaults = defaultModelConfigValues.biased as Omit<
      BiasedProductionStreamConfigData,
      'stream'
    >;
    return {
      ...config,
      probabilityDistance:
        config.probabilityDistance || defaults.probabilityDistance,
      residualSigma: config.residualSigma || defaults.residualSigma,
      constrainEur: config.constrainEur || defaults.constrainEur,
      distributions: config.distributions || defaults.distributions,
    };
  }

  if (config.modelType === 'early_time') {
    const defaults = defaultModelConfigValues.early_time as Omit<
      EarlyTimeProductionStreamConfigData,
      'stream'
    >;
    return {
      ...config,
      probabilityDistance:
        config.probabilityDistance || defaults.probabilityDistance,
      residualSigma: config.residualSigma || defaults.residualSigma,
      eurToleranceFactor:
        config.eurToleranceFactor || defaults.eurToleranceFactor,
      lateralLengthDegradationFactor:
        config.lateralLengthDegradationFactor ||
        defaults.lateralLengthDegradationFactor,
      lateralLengthDegradationFt:
        config.lateralLengthDegradationFt ||
        defaults.lateralLengthDegradationFt,
    };
  }

  if (config.modelType === 'ramp_up') {
    const defaults = defaultModelConfigValues.ramp_up as Omit<
      RampUpProductionStreamConfigData,
      'stream'
    >;
    return {
      ...config,
      earlyTimeRampUpOnly:
        config.earlyTimeRampUpOnly || defaults.earlyTimeRampUpOnly,
    };
  }

  return config;
};

export const sanitizePayload = <T>(payload: T): T => {
  if (payload === null || payload === undefined) return payload;

  if (Array.isArray(payload)) {
    return payload
      .filter((item) => item !== null)
      .map((item) => sanitizePayload(item)) as T;
  }

  if (typeof payload === 'object') {
    return Object.fromEntries(
      Object.entries(payload as Record<string, unknown>)
        .filter(([, value]) => value !== null)
        .map(([key, value]) => [key, sanitizePayload(value)])
    ) as T;
  }

  return payload;
};

export const getValueByPath = (
  obj: Record<string, unknown> | unknown[],
  path: string
): unknown => {
  return path.split('.').reduce<unknown>((current, segment) => {
    if (current === null || current === undefined) return undefined;
    const index = /^\d+$/.test(segment) ? Number(segment) : segment;
    return (current as Record<string, unknown>)[index as string];
  }, obj);
};
